import FormControl, { type FormControlProps } from '@mui/material/FormControl';
import FormControlLabel, {
  type FormControlLabelProps,
} from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel, { type FormLabelProps } from '@mui/material/FormLabel';
import MuiRadio from '@mui/material/Radio';
import RadioGroup, { type RadioGroupProps } from '@mui/material/RadioGroup';
import { type ReactNode, useId } from 'react';
import {
  type FieldValues,
  type UseControllerProps,
  useController,
} from 'react-hook-form';

type RadioOption = {
  value: string;
  label: string;
};

type RHFRadioGroupProps<
  TField extends FieldValues,
  Option extends object | RadioOption,
> = {
  label?: FormControlLabelProps['label'];
  id?: string;
  controlProps: UseControllerProps<TField>;
  options: Option[];
  containerProps?: FormControlProps;
  labelProps?: FormLabelProps;
  radioGroupProps?: RadioGroupProps;
  optionIdKey?: keyof Option;
  optionTextKey?: keyof Option;
  disabled?: boolean;
  renderOption?: (
    option: Option,
    renderRadio: (props: Partial<FormControlLabelProps>) => React.ReactNode,
  ) => ReactNode;
};

export const RHFRadioGroup = <
  TField extends FieldValues,
  Option extends object | RadioOption,
>({
  id,
  label,
  containerProps,
  labelProps,
  controlProps,
  radioGroupProps,
  options,
  optionIdKey = 'value' as keyof Option,
  optionTextKey = 'label' as keyof Option,
  renderOption,
  disabled,
}: RHFRadioGroupProps<TField, Option>) => {
  const autoId = useId();
  const radioId = id ?? autoId;

  const {
    field: { value, onChange, ...field },
    fieldState: { error },
  } = useController(controlProps);

  return (
    <FormControl
      error={!!error}
      disabled={disabled ?? false}
      {...containerProps}
    >
      {label && (
        <FormLabel id={radioId} {...labelProps}>
          {label}
        </FormLabel>
      )}

      <RadioGroup
        aria-labelledby={label ? radioId : undefined}
        aria-describedby={error ? `${radioId}-helper` : undefined}
        {...field}
        {...radioGroupProps}
        value={value ?? ''}
        onChange={(e, v) => {
          onChange(v);
          radioGroupProps?.onChange?.(e, v);
        }}
      >
        {options.map((option) => {
          const sharedProps = {
            value: option[optionIdKey],
            label: option[optionTextKey] as string,
            control: <MuiRadio />,
          };

          if (renderOption) {
            return renderOption(option, (props) => (
              <FormControlLabel
                {...props}
                {...sharedProps}
                key={option[optionIdKey] as string}
              />
            ));
          }

          return (
            <FormControlLabel
              {...sharedProps}
              key={option[optionIdKey] as string}
            />
          );
        })}
      </RadioGroup>
      {error?.message && (
        <FormHelperText id={`${radioId}-helper`}>
          {error?.message}
        </FormHelperText>
      )}
    </FormControl>
  );
};
